import React, { useEffect, useRef, useState } from 'react'
import { Head, Link } from '@inertiajs/react'
import DashboardLayout from '@/frontend/layouts/DashboardLayout'
import { Button } from '@/components/ui/button'
import { ArrowLeft, LoaderCircle, Maximize2, Minimize2, Monitor, MousePointer2, RotateCw } from 'lucide-react'
import { useDesktopViewer, type DesktopViewerProps } from '@/frontend/pages/Dashboard/useDesktopViewer'

function DesktopLab(props: DesktopViewerProps) {
  const { worker, desktop, labels, viewer_url, error } = props
  const viewer = useDesktopViewer(props)
  const stage = useRef<HTMLElement>(null)
  const [fullscreen, setFullscreen] = useState(false)
  const [fullscreenError, setFullscreenError] = useState(false)
  const [waiting, setWaiting] = useState(false)
  useEffect(() => {
    const changed = () => setFullscreen(document.fullscreenElement === stage.current)
    document.addEventListener('fullscreenchange', changed)
    return () => document.removeEventListener('fullscreenchange', changed)
  }, [])
  useEffect(() => {
    setWaiting(false)
    if (viewer.current) return
    const timer = window.setTimeout(() => setWaiting(true), 8000)
    return () => window.clearTimeout(timer)
  }, [viewer.current, viewer.mode, desktop?.control.generation])
  const expand = async () => {
    try {
      setFullscreenError(false)
      if (document.fullscreenElement) await document.exitFullscreen()
      else await stage.current?.requestFullscreen()
    } catch { setFullscreenError(true) }
  }
  return (
    <main ref={stage} data-testid="desktop-stage" className="flex h-full min-h-0 w-full flex-col bg-background">
      <Head title={labels.title} />
      <header className="flex min-h-14 shrink-0 items-center justify-between gap-3 border-b px-4 py-2.5">
        <div className="flex min-w-0 items-center gap-2.5">
          <Monitor className="size-4 shrink-0 text-muted-foreground" />
          <h1 className="truncate text-sm font-medium">{worker.name}</h1>
        </div>
        <div className="flex shrink-0 items-center gap-1.5">
          <Link href={`/dashboard/digital_workers/${worker.id}/chat`} title={labels.chat} aria-label={labels.chat}
            className="inline-flex size-9 items-center justify-center rounded-md text-muted-foreground hover:bg-accent hover:text-foreground">
            <ArrowLeft className="size-4" />
          </Link>
          <Button variant="ghost" size="icon" onClick={expand} aria-label={fullscreen ? labels.exit_fullscreen : labels.fullscreen} title={fullscreen ? labels.exit_fullscreen : labels.fullscreen}>
            {fullscreen ? <Minimize2 className="size-4" /> : <Maximize2 className="size-4" />}
          </Button>
          {viewer.mode === 'human'
            ? <Button disabled={viewer.busy} onClick={() => viewer.change('return')}>{labels.return}</Button>
            : <Button disabled={viewer.busy || !desktop || viewer.mode === 'pending'} onClick={() => viewer.change('take')}>
              {viewer.mode === 'pending' ? <LoaderCircle className="mr-2 size-4 animate-spin" /> : <MousePointer2 className="mr-2 size-4" />}
              {viewer.mode === 'pending' ? labels.pending : labels.take}
            </Button>}
        </div>
      </header>
      {fullscreenError && <p role="alert" className="px-4 py-2 text-sm text-destructive">{labels.fullscreen_error}</p>}
      {error && <p role="alert" className="px-4 py-2 text-sm text-destructive">{labels.connection_error}</p>}
      <div className="relative min-h-0 flex-1 overflow-hidden bg-zinc-900">
        {viewer.current && viewer_url
          ? <iframe ref={viewer.frame} key={`${worker.id}-${desktop?.control.generation}-${viewer.access}`} title={labels.title}
              src={viewer_url} className="absolute inset-0 h-full w-full border-0" allow="clipboard-read; clipboard-write" />
          : <div className="absolute inset-0 flex flex-col items-center justify-center gap-4 px-6 text-center text-sm text-zinc-300" role="status">
              {!waiting && <LoaderCircle className="size-5 animate-spin text-zinc-500" />}
              <p>{viewer.mode === 'pending' ? labels.pending : waiting || error ? labels.connection_error : labels.opening}</p>
              {(waiting || error) && viewer.access && <Button variant="secondary" disabled={viewer.busy} onClick={viewer.refresh}><RotateCw className="mr-2 size-4" />{labels.retry}</Button>}
            </div>}
      </div>
    </main>
  )
}
DesktopLab.layout = (page: React.ReactElement) => <DashboardLayout variant="canvas">{page}</DashboardLayout>
export default DesktopLab
